/**
 * Hálózati katalógus — /halozat/katalog
 * Eladó megosztott termékeinek böngészése + közvetlen megrendelés leadása
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { get, post } from '../../lib/api';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import NumInput from '../../components/ui/NumInput';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';

interface CatalogItem {
  id: string;
  sku: string | null;
  name: string;
  unit: string;
  net_unit_price: number;   // fillérben (pl. 42500 = 425,00 Ft)
  vat_rate: number;
  description: string | null;
  custom_price: number | null; // fillérben
}

interface CatalogResponse {
  share: {
    from_tenant_id: string;
    share_type: string;
    discount_percent: number;
    from_name?: string;
  } | null;
  items: CatalogItem[];
}

interface CartItem { item: CatalogItem; qty: number }

// HUF decimal megjelenítés (a katalógus már forintban adja vissza az árakat)
function fmt(amount: number) {
  return new Intl.NumberFormat('hu-HU').format(Math.round(amount)) + ' Ft';
}

export default function NetworkCatalogPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sellerTenantId = params.get('seller');

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showOrder, setShowOrder] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!sellerTenantId) return;
    setLoading(true);
    try {
      const data = await get<CatalogResponse>(`/network/catalog/${sellerTenantId}`);
      setCatalog(data);
    } catch {
      toast('Hiba a katalógus betöltésénél.', 'error');
    } finally {
      setLoading(false);
    }
  }, [sellerTenantId, toast]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const filtered = useMemo(() => {
    if (!catalog?.items) return [];
    const q = search.toLowerCase();
    return catalog.items.filter(i =>
      i.name.toLowerCase().includes(q) || (i.sku || '').toLowerCase().includes(q),
    );
  }, [catalog, search]);

  const setQty = (item: CatalogItem, qty: number) => {
    if (qty <= 0) {
      setCart(c => c.filter(ci => ci.item.id !== item.id));
    } else {
      setCart(c => {
        const existing = c.find(ci => ci.item.id === item.id);
        if (existing) return c.map(ci => ci.item.id === item.id ? { ...ci, qty } : ci);
        return [...c, { item, qty }];
      });
    }
  };

  const getQty = (itemId: string) => cart.find(ci => ci.item.id === itemId)?.qty || 0;

  // Kosár összesen — fillérben számolunk, megjelenítéskor /100
  const cartTotalFillér = cart.reduce((sum, ci) => {
    const price = ci.item.custom_price ?? ci.item.net_unit_price;
    return sum + price * ci.qty;
  }, 0);

  const handlePlaceOrder = async () => {
    if (!sellerTenantId || cart.length === 0) return;
    setSubmitting(true);
    try {
      await post('/network/orders', {
        sellerTenantId,
        items: cart.map(ci => ({
          seller_item_id: ci.item.id,
          description: ci.item.name,
          quantity: ci.qty,
          unit: ci.item.unit,
          net_unit_price: ci.item.custom_price ?? ci.item.net_unit_price, // fillér
          vat_rate: ci.item.vat_rate,
        })),
        note: orderNote || undefined,
      });
      toast('Megrendelés leadva! Az eladó értesítést kapott.', 'success');
      setCart([]);
      setShowOrder(false);
      setOrderNote('');
      navigate('/halozat');
    } catch (e: any) {
      toast(e?.message || 'Hiba a megrendelés leadásánál.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!sellerTenantId) {
    return (
      <div>
        <PageHeader title="Hálózati katalógus" />
        <EmptyState
          icon="🔗"
          title="Nincs megadva partner"
          description="Válassz egy Modulum hálózati partnert a katalógus megtekintéséhez."
          action={<Link to="/halozat"><Button variant="ghost">← Vissza a hálózathoz</Button></Link>}
        />
      </div>
    );
  }

  const sellerName = catalog?.share?.from_name || 'Partner';
  const discount = catalog?.share?.discount_percent || 0;

  return (
    <div>
      <PageHeader
        title={catalog?.share ? `${sellerName} katalógusa` : 'Hálózati katalógus'}
        description={discount > 0 ? `${discount}% kedvezmény alkalmazva az árakra` : undefined}
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input placeholder="Keresés termékben…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {cart.length > 0 && (
          <Button variant="primary" onClick={() => setShowOrder(true)}>
            🛒 Kosár ({cart.length} tétel — {fmt(cartTotalFillér)})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : !catalog?.share ? (
        <EmptyState
          icon="🔒"
          title="Nincs megosztott árlista"
          description="Ez a partner még nem osztott meg árlistát veled."
          action={<Link to="/halozat"><Button variant="ghost">← Vissza a hálózathoz</Button></Link>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon="🔍" title="Nincs találat" description="Módosítsd a keresési feltételt." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {filtered.map(item => {
            const price = item.custom_price ?? item.net_unit_price;
            const hasDiscount = item.custom_price !== null && item.custom_price !== item.net_unit_price;
            const qty = getQty(item.id);

            return (
              <div
                key={item.id}
                className="card"
                style={{
                  padding: '14px 16px',
                  border: qty > 0 ? '2px solid var(--orange)' : undefined,
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 2 }}>{item.name}</div>
                    {item.sku && <div style={{ fontSize: 11, color: 'var(--slate-2)' }}>#{item.sku}</div>}
                    {item.description && (
                      <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 4, lineHeight: 1.4 }}>{item.description}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, color: 'var(--orange)', fontSize: 15 }}>{fmt(price)}</div>
                    {hasDiscount && (
                      <div style={{ fontSize: 11, color: 'var(--slate-2)', textDecoration: 'line-through' }}>{fmt(item.net_unit_price)}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--slate-2)' }}>+ ÁFA ({item.vat_rate}%)</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <NumInput
                    value={qty || ''}
                    onChange={v => setQty(item, Number(v) || 0)}
                    placeholder="0"
                    style={{ width: 70 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--slate)' }}>{item.unit}</span>
                  {qty === 0 ? (
                    <Button size="sm" variant="ghost" onClick={() => setQty(item, 1)} style={{ marginLeft: 'auto' }}>
                      + Kosárba
                    </Button>
                  ) : (
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--green)', fontWeight: 700 }}>✓ Kosárban</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Megrendelés modal ── */}
      <Modal isOpen={showOrder} title="Megrendelés összesítő" onClose={() => setShowOrder(false)}>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--slate-2)' }}>
                <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 700 }}>Termék</th>
                <th style={{ textAlign: 'right', fontWeight: 700 }}>Menny.</th>
                <th style={{ textAlign: 'right', fontWeight: 700 }}>Egységár</th>
                <th style={{ textAlign: 'right', fontWeight: 700 }}>Összesen</th>
              </tr>
            </thead>
            <tbody>
              {cart.map((ci, i) => {
                const price = ci.item.custom_price ?? ci.item.net_unit_price;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line-2)' }}>
                    <td style={{ padding: '7px 0', color: 'var(--navy)' }}>{ci.item.name}</td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{ci.qty} {ci.item.unit}</td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{fmt(price)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>{fmt(price * ci.qty)}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={3} style={{ padding: '10px 0 4px', fontWeight: 700, color: 'var(--slate)', fontSize: 12 }}>
                  Nettó összesen
                </td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--orange)', fontSize: 16, padding: '10px 0 4px' }}>
                  {fmt(cartTotalFillér)}
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.07em' }}>
              Megjegyzés (opcionális)
            </label>
            <textarea
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                border: '1.5px solid var(--line)', borderRadius: 8,
                fontSize: 13, resize: 'vertical', minHeight: 64,
                fontFamily: 'inherit', color: 'var(--navy)', outline: 'none',
              }}
              value={orderNote}
              onChange={e => setOrderNote(e.target.value)}
              placeholder="Pl. sürgős szállítás, különleges kérés…"
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowOrder(false)}>Mégse</Button>
            <Button variant="primary" onClick={handlePlaceOrder} loading={submitting}>
              Megrendelés leadása
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
